import { url } from "inspector";
import {
  CompanyType,
  DateType,
  RepresentativeEntityType,
  TrustType,
} from "../constants/onboarding.constants";
import { baseQuery } from "./baseQuery";
import { createApi } from "@reduxjs/toolkit/query/react";
import {
  IKYCDetail,
  IRepresentativeCompany,
  IRepresentativePerson,
  IRepresentativeTrust,
  IUpdateBusinessDetail,
  IUpdateRepresentativeCompany,
  IUpdateRepresentativePerson,
  IUpdateRepresentativeTrust,
} from "../shared/models/onboarding.modal";
import * as XLSX from "xlsx";
import { FileName, FileFormat } from "../constants/common.constants";
import moment from "moment";
import { FormatDateApi, FormatDateType } from "shared/models";
import { convertStatusKYC, getFileNameFromContentDisposition } from "shared/helpers/utilities";

export const kycService = createApi({
  reducerPath: "kycService",
  baseQuery: baseQuery,
  endpoints: (builder) => ({
    getKYCs: builder.query<
      any,
      {
        partners: number[];
        case_status: string[];
        status: string[];
        date_from: string;
        date_to: string;
        search: string;
        page: number;
        limit: number;
        path_name: string;
      }
    >({
      query: ({ path_name, ...params }) => ({
        url: `/report/kyc`,
        params: params,
        method: "GET",
        headers: {
          PageName: path_name,
        },
      }),
    }),
    getKYCRequestVolume: builder.query<
      any,
      {
        date_from: string;
        date_to: string;
        partners: number[];
      }
    >({
      query: ({...params }) => ({
        url: `/report/kyc/requested-volume`,
        params: params,
        method: "GET",
        headers: {
          PageName: 'KYC Management',
        },
      }),
    }),
    getKYCRequestStatusDistribution: builder.query<
      any,
      {
        date_from: string;
        date_to: string;
        partners: number[];
      }
    >({
      query: ({...params }) => ({
        url: `/report/kyc/request-status-distribution`,
        params: params,
        method: "GET",
        headers: {
          PageName: 'KYC Management',
        },
      }),
    }),

    downloadListKyc: builder.query<
      any,
      {
        partners: number[];
        status: string[];
        date_from: string;
        date_to: string;
        search: string;
        path_name: string;
        role_user: string;
        format?: string;
      }
    >({
      // KYC report uses a different backend contract from the other reports: it streams a CSV blob directly (backend only produces CSV).
      // `format` is kept out of the request params — for XLSX the CSV is converted to a workbook client-side. role_user forwards the admin split-view choice.
      query: ({ path_name, format, ...params }) => ({
        url: `/report/kyc/download`,
        params: { path_name, ...params },
        method: "GET",
        headers: {
          PageName: path_name,
        },
        responseType: "blob",
        responseHandler: async (response) => {
          // Prefer the filename the backend sets via Content-Disposition (must be CORS-exposed); fall back to the FE-built name.
          const backendFileName = getFileNameFromContentDisposition(response.headers.get("content-disposition"));
          const fallbackBaseName = FileName.KYCListingCsv.replace(/\s+/g, '_') + `_${moment().format(FormatDateApi.YYYYMMDD)}`;

          if (format === FileFormat.XLSX) {
            // Backend only streams CSV, so build the XLSX workbook from that CSV text in the browser.
            const csvText = await response.text();
            const workbook = XLSX.read(csvText, { type: "string", raw: true });
            const xlsxData = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
            const xlsxBlob = new Blob([xlsxData], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const xlsxUrl = window.URL.createObjectURL(xlsxBlob);
            // Reuse the backend base name but swap the extension to .xlsx.
            const exportedFileName =
              (backendFileName ? backendFileName.replace(/\.[^.]+$/, "") : fallbackBaseName) + `.xlsx`;
            const xlsxLink = document.createElement("a");
            xlsxLink.href = xlsxUrl;
            xlsxLink.setAttribute("download", exportedFileName);
            xlsxLink.setAttribute("target", "_blank");
            document.body.appendChild(xlsxLink);
            xlsxLink.click();
            // Return the resolved filename so the download hook can show it in the success notification.
            return { exportedFileName };
          }

          const url = window.URL.createObjectURL(await response.blob());
          const link = document.createElement("a");
          link.href = url;
          const exportedFileName = backendFileName || (fallbackBaseName + `.csv`);
          link.setAttribute("download", exportedFileName);
          link.setAttribute("target", "_blank");
          document.body.appendChild(link);
          link.click();
          // Return the resolved filename so the download hook can show it in the success notification.
          return { exportedFileName };
        },
      }),
    }),

    getCallout: builder.query<
      any,
      {
        partners: number[];
        date_from: string;
        date_to: string;
        path_name: string;
      }
    >({
      query: ({ path_name, ...params }) => ({
        url: `/report/kyc/callout-box`,
        params: params,
        method: "GET",
        headers: {
          PageName: path_name,
        },
      }),
    }),

    updateKYCStatus: builder.mutation<
      any,
      {
        id: number;
        status: string;
        reason_cancellation: any;
        detail_other_cancellation: any;
      }
    >({
      query: ({ id, status, reason_cancellation, detail_other_cancellation }) => ({
        url: `/report/kyc/${id}/status`,
        body: { status, reason_cancellation, detail_other_cancellation },
        method: "PUT",
        headers: {
          PageName: "KYC Management",
        },
      }),
    }),

    getKYCById: builder.query<
      any,
      {
        id: number;
        path_name: string;
      }
    >({
      query: ({ id, path_name }) => ({
        url: `/report/kyc/${id}`,
        method: "GET",
        headers: {
          PageName: path_name,
        },
      }),
      transformResponse: (rawData: any) => {
        if (rawData.success) {
          return rawData.data;
        }
      },
    }),

    getBeneficalsOwners: builder.query<
      any,
      {
        id: number;
        path_name: string;
      }
    >({
      query: ({ id, path_name }) => ({
        url: `/report/kyc/${id}/beneficals-owners`,
        method: "GET",
        headers: {
          PageName: path_name,
        },
      }),
      transformResponse: (rawData: any) => {
        if (rawData.success) {
          return rawData.data;
        }
      },
    }),

    getAuthorisedExecutives: builder.query<
      any,
      {
        id: number;
        path_name: string;
      }
    >({
      query: ({ id, path_name }) => ({
        url: `/report/kyc/${id}/authorised-executives`,
        method: "GET",
        headers: {
          PageName: path_name,
        },
      }),
      transformResponse: (rawData: any) => {
        if (rawData.success) {
          return rawData.data;
        }
      },
    }),

    createRepresentativeTrust: builder.mutation<any, IRepresentativeTrust>({
      query: ({ kycId, representative_type, ...body }) => ({
        url: `/report/kyc/${kycId}/${representative_type}`,
        body: body,
        method: "POST",
        headers: {
          PageName: "KYC Management",
        },
      }),
    }),

    updateRepresentativeTrust: builder.mutation<any, IUpdateRepresentativeTrust>({
      query: ({ kycId, representativeId, representative_type, ...body }) => ({
        url: `/report/kyc/${kycId}/${representative_type}/${representativeId}`,
        body: body,
        method: "PUT",
        headers: {
          PageName: "KYC Management",
        },
      }),
    }),

    createRepresentativeCompany: builder.mutation<any, IRepresentativeCompany>({
      query: ({ kycId, representative_type, ...body }) => ({
        url: `/report/kyc/${kycId}/${representative_type}`,
        body: body,
        method: "POST",
        headers: {
          PageName: "KYC Management",
        },
      }),
    }),

    createRepresentativePerson: builder.mutation<any, IRepresentativePerson>({
      query: ({ kycId, representative_type, ...body }) => ({
        url: `/report/kyc/${kycId}/${representative_type}`,
        body: body,
        method: "POST",
        headers: {
          PageName: "KYC Management",
        },
      }),
    }),

    updateRepresentativePerson: builder.mutation<any, IUpdateRepresentativePerson>({
      query: ({ kycId, representative_type, representativeId, ...body }) => ({
        url: `/report/kyc/${kycId}/${representative_type}/${representativeId}`,
        body: body,
        method: "PUT",
        headers: {
          PageName: "KYC Management",
        },
      }),
    }),

    updateRepresentativeCompany: builder.mutation<any, IUpdateRepresentativeCompany>({
      query: ({ kycId, representative_type, representativeId, ...body }) => ({
        url: `/report/kyc/${kycId}/${representative_type}/${representativeId}`,
        body: body,
        method: "PUT",
        headers: {
          PageName: "KYC Management",
        },
      }),
    }),

    deleteRepresentative: builder.mutation<
      any,
      {
        kycId: number;
        representativeId: number;
        representative_type: "beneficals-owners" | "authorised-executives";
      }
    >({
      query: ({ kycId, representativeId, representative_type }) => ({
        url: `/report/kyc/${kycId}/${representative_type}/${representativeId}`,
        method: "DELETE",
        headers: {
          PageName: "KYC Management",
        },
      }),
    }),

    updateBusinessDetail: builder.mutation<any, IUpdateBusinessDetail>({
      query: ({ merchantId, ...body }) => ({
        url: `/report/kyc/${merchantId}`,
        body: body,
        method: "PUT",
        headers: {
          PageName: "KYC Management",
        },
      }),
    }),

    updateComment: builder.mutation<any, any>({
      query: ({ kycId, ...body }) => ({
        url: `/report/kyc/${kycId}/comment`,
        body: body,
        method: "PUT",
        headers: {
          PageName: "KYC Management",
        },
      }),
    }),

    updateKYCUseBOInfo: builder.mutation<any, {kycId: number, is_use_bo_info_for_ae: boolean}>({
      query: ({ kycId, is_use_bo_info_for_ae }) => ({
        url: `/report/kyc/${kycId}/updateUseBoInfo`,
        body: { is_use_bo_info_for_ae: is_use_bo_info_for_ae },
        method: "PUT",
        headers: {
          PageName: "KYC Management",
        },
      }),
    }),

    downloadKYCReview: builder.query<
      any,
      {
        kycId: number;
        path_name: string;
      }
    >({
      query: ({ kycId, path_name }) => ({
        url: `/report/kyc/${kycId}/download-review`,
        method: "POST",
        responseType: "blob",
        headers: {
          PageName: path_name,
        },
        responseHandler: async (response) => {
          const url = window.URL.createObjectURL(await response.blob());
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", FileName.KYCReviewPdf.replace(/\s+/g, '_')+`_${moment().format(FormatDateApi.YYYYMMDD)}.`+`pdf`);
          link.setAttribute("target", "_blank");
          document.body.appendChild(link);
          link.click();
          return response;
        },
      }),
    }),
    downloadSignupInformation: builder.query<
      any,
      {
        merchantId: number;
        path_name: string;
      }
    >({
      query: ({ merchantId, path_name }) => ({
        url: `/report/kyc/${merchantId}/kyc-information-file`,
        method: "get",
        responseType: "blob",
        headers: {
          PageName: path_name,
        },
        responseHandler: async (response) => {
          const contentType = response.headers.get("Content-Type");

          if (contentType?.includes("application/pdf")) {
            //handle PDF download
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
              "download",
              FileName.KYCSignupInfo.replace(/\s+/g, "_") +
                `_${moment().format(FormatDateApi.YYYYMMDD)}.pdf`
            );
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            return { success: true };
          } else {
            //handle JSON error
            const data = await response.json();
            return data; // { success: false, message: "No file to download." }
          }
        },
      }),
    }),
    reSendEmail: builder.mutation<any, any>({
      query: ({ kycId }) => ({
        url: `/report/kyc/${kycId}/resend-email`,
        method: "POST",
        headers: {
          PageName: "KYC Management",
        },
      }),
    }),
    getKycInfomationFileStatus: builder.query<any, {merchantId: any}>({
      query: ({merchantId}) => ({
        url: `/report/kyc/${merchantId}/kyc-information-file-status`,
        method: "GET",
        headers: {
          PageName: 'KYC Management',
        },
      }),
    }),
  }),
});

export const {
  useGetKYCsQuery,
  useLazyDownloadListKycQuery,
  useGetCalloutQuery,
  useUpdateKYCStatusMutation,
  useGetKYCByIdQuery,
  useLazyGetKYCByIdQuery,
  useGetAuthorisedExecutivesQuery,
  useGetBeneficalsOwnersQuery,
  useCreateRepresentativeCompanyMutation,
  useCreateRepresentativeTrustMutation,
  useCreateRepresentativePersonMutation,
  useUpdateRepresentativeTrustMutation,
  useUpdateRepresentativePersonMutation,
  useUpdateRepresentativeCompanyMutation,
  useDeleteRepresentativeMutation,
  useUpdateBusinessDetailMutation,
  useUpdateCommentMutation,
  useUpdateKYCUseBOInfoMutation,
  useLazyDownloadKYCReviewQuery,
  useGetKYCRequestVolumeQuery,
  useGetKYCRequestStatusDistributionQuery,
  useReSendEmailMutation,
  useLazyDownloadSignupInformationQuery,
  useLazyGetKycInfomationFileStatusQuery,
} = kycService;
